import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import { handleError, formatDate2, formatDateHour } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useNavigate } from 'react-router-dom';
import ListItemSupplyEdit from '../../layouts/ListItemSupplyEdit';
import { fetchReport } from '../../utils/lib';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const SupplyCommonReport = ({ reportType, title }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  
  // Dialog states
  const [completeDialog, setCompleteDialog] = useState({
    open: false,
    item: null
  });
  
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    item: null
  });

  useEffect(() => {
    fetchData(page);
  }, [reportType]);

  async function fetchData(pageNumber) {
    setIsLoading(true);
    try {
      const response = await fetchReport(reportType, pageNumber);
      //console.log(response);
      setData(response);
      setPage(pageNumber);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  }
  
  const handlePageChange = (event, value) => {
    fetchData(value);
  };
  
  // Handle completing
  const handleComplete = (supply) => {
    setCompleteDialog({
      open: true,
      item: supply
    });
  };
  
  const handleCompleteClose = () => {
    setCompleteDialog({
      open: false,
      item: null
    });
  };
  
  const handleCompleteConfirm = async () => {
    try {
      setIsLoading(true);
      const { item } = completeDialog;

      await axios.put(
        `${import.meta.env.VITE_API_URL}pets_supply?id=${item.id}&check`,
        {},
        { withCredentials: true }
      );

      // Refresh the data
      fetchData(page);
      snackbar(t('reports.supplyCompleted'));
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
      handleCompleteClose();
    }
  };

  // Handle deleting
  const handleDelete = (supply) => {
    setDeleteDialog({
      open: true,
      item: supply
    });
  };
  
  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false,
      item: null
    });
  };
  
  const handleDeleteConfirm = async () => {
    try {
      setIsLoading(true);
      const { item } = deleteDialog;

      await axios.delete(
        `${import.meta.env.VITE_API_URL}pets_supply?id=${item.id}`,
        { withCredentials: true }
      );

      // Refresh the data
      fetchData(page);
      snackbar(t('reports.supplyDeleted'));
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
      handleDeleteClose();
    }
  };

  const handleRowClick = (supply) => {
    // Navigate to pet profile
    navigate('/main/view-pet', { state: { id: supply.pet.id, updateLastView: false } });
  };

  // Función para obtener el primer propietario de una mascota
  const getFirstOwner = (pet) => {
    return pet?.owners && pet.owners.length > 0 ? pet.owners[0] : null;
  };

  return (
    <Box>
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2}>
                <Typography variant="h6">
                  {title || t('reports.supplyTitle')} ({data.total_results || data.content.length})
                </Typography>
              </Box>
              
              <Container maxWidth="xl" sx={{ mt: 2, px: 0 }}>
                {data.content.map((supply, index) => {
                  const firstOwner = getFirstOwner(supply.pet);
                  return (
                    <Box key={supply.id}>
                      <ListItemSupplyEdit
                        product={supply.product?.name || t('reports.productUnavailable')}
                        description={`${formatDate2(supply.date_tentative)} ${supply.date_supply ? t('reports.supplied') : t('reports.notSupplied')} - ${
                          firstOwner
                            ? t('reports.petOfOwnerLabel', { pet: supply.pet.name, owner: firstOwner.name })
                            : supply.pet.name
                        }`}
                        thumbUrl={supply.product?.thumb_url}
                        avatarContent={<InventoryIcon sx={{ fontSize: 20 }} />}
                        onMarkSupplied={!supply.date_supply ? () => handleComplete(supply) : undefined}
                        onDelete={() => handleDelete(supply)}
                        onClick={() => handleRowClick(supply)}
                        dateSupply={supply.date_supply}
                        dateTentative={supply.date_tentative}
                        index={index}
                      />
                    </Box>
                  );
                })}
              </Container>
              
              {data.total_pages && data.total_pages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={data.total_pages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noSupplies')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('common.loading')}</Typography>
        </Box>
      )}
      
      {/* Complete Dialog */}
      <Dialog
        open={completeDialog.open}
        onClose={handleCompleteClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('reports.confirmSupply')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('reports.confirmSupplyDesc')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCompleteClose} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleCompleteConfirm} color="primary" autoFocus>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('reports.deleteSupplyConfirm')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('reports.deleteSupplyIrreversible')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} color="primary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplyCommonReport; 